import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import Confirm from '@common/components/confirm-button';
import Dropdown from '@common/components/dropdown';
import { EmojiReactionPicker } from '@common/components/emoji-reaction-picker';
import { EmojiReactionsUserList } from '@common/components/emoji-reactions-user-list';
import Icon from '@common/components/icon';

// Types
import { AllowedEmoji, LoggedUser } from '@common/types/objects';
import { PrivateMessage } from '@modules/chat/types/objects';
import { EditMessageModal } from '../message/edit-message';
import { EMessageTypes } from '@modules/chat/definitions';

interface MessageActionsProps {
  message: PrivateMessage;
  conversationId: string;
  allowedEmojis: AllowedEmoji[];
  canDelete: boolean;
  canEdit: boolean;
  setModalOpen: (open: boolean) => void;
  setShowSettings: (open: boolean) => void;
  onReply: (messageId: string) => void;
  onRemove: (messageId: string) => void;
  setDropdownOpen: (open: boolean) => void;
  onSetEmojiReaction: (messageId: string, emoji: AllowedEmoji, isSelected: boolean) => void;
  onEdit: (message: PrivateMessage) => void;
  loggedUser: LoggedUser;
  handleRetryMessage: (message: PrivateMessage) => void;
  isDropdownOpen: boolean;
  isAttachment?: boolean;
}

const MessageActions = ({
  message, allowedEmojis, conversationId, canDelete, canEdit,
  onReply, onRemove, setDropdownOpen, setModalOpen, setShowSettings, onSetEmojiReaction,
  onEdit, loggedUser, handleRetryMessage, isDropdownOpen, isAttachment = true
}: MessageActionsProps) => {
  const { t } = useTranslation();
  const [isEmojisModalOpen, setIsEmojiModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleAddNewReaction = useCallback((emoji: AllowedEmoji) => {
    // eslint-disable-next-line camelcase
    const isAlreadySelected = message.reactions.find(({ short_name, is_selected_by_user }) => (
      // eslint-disable-next-line camelcase
      short_name === emoji.short_name && is_selected_by_user
    ));

    onSetEmojiReaction(message.id, emoji, !isAlreadySelected);
  }, [onSetEmojiReaction, message.id, message.reactions]);

  return (
    <>
      {isAttachment && (
        <div className="PrivateMessage__EmojiReactionPicker">
          <EmojiReactionPicker
            allowedEmojis={allowedEmojis}
            onSelect={handleAddNewReaction}
          />
        </div>
      )}
      <div className={`${isAttachment ? 'PrivateMessage__OptionsOverlay' : 'PrivateMessage__OptionsOverlay__Attachment'}`}>
        <Dropdown
          alignRight
          className="PrivateMessage__OptionsDropdown"
          onOpen={() => setDropdownOpen(true)}
          onClose={() => setDropdownOpen(false)}
          trigger={(
            <div className="PrivateMessage__Options">
              <Icon type="chevron_left" />
            </div>
          )}
          open={isDropdownOpen}
        >
          {message.type !== EMessageTypes.FAILED ? (
            <Dropdown.Item
              onClick={() => onReply(message.id)}
            >
              {t('chat:reply')}
            </Dropdown.Item>
          ) : (
            <Dropdown.Item
              onClick={() => handleRetryMessage(message)}
            >
              {t('chat:retry')}
            </Dropdown.Item>
          )}
          {message.reactions.length > 0 && (
            <EmojiReactionsUserList
              show={isEmojisModalOpen}
              onSetVisibility={setIsEmojiModalOpen}
              messageId={message.id}
              conversationId={conversationId}
              emojiReactions={message.reactions}
            >
              <Dropdown.Item>
                {t('chat:reactions')}
              </Dropdown.Item>
            </EmojiReactionsUserList>
          )}
          {message.type !== EMessageTypes.FAILED && canEdit && (
            <EditMessageModal
              message={message}
              shouldShow={isEditModalOpen}
              loggedUser={loggedUser}
              onShow={() => setIsEditModalOpen(true)}
              onHide={() => {
                setIsEditModalOpen(false);
                setShowSettings(false);
                setDropdownOpen(false);
                setModalOpen(false);
              }}
              onEdit={onEdit}
            >
              <Dropdown.Item>
                {t('chat:edit')}
              </Dropdown.Item>
            </EditMessageModal>
          )}
          {canDelete && (
            <Confirm
              title={t('chat:confirm_remove_message')}
              onEnter={() => setModalOpen(true)}
              onConfirm={() => onRemove(message.id)}
              onExit={() => {
                setModalOpen(false);
                setShowSettings(false);
              }}
            >
              <Dropdown.Item danger>
                {t('chat:remove_message')}
              </Dropdown.Item>
            </Confirm>
          )}
        </Dropdown>
      </div>
    </>
  );
};

export default MessageActions;
